// src/pages/Orders.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { syncAll, getLastSyncInfo } from '../services/orderService';
import { fetchOrdersFromFirestore } from '../services/orderService.firestore';
import { useAuth } from '../context/AuthContext';
import { OrderAdapter } from '../adapters/OrderAdapter';
import LoadingScreen from '../components/LoadingScreen';
import { Button, IconButton, Stack, Menu, MenuItem, Typography, Alert, Box, Grid, Paper, CircularProgress, Chip, ToggleButton, ToggleButtonGroup, useTheme, Snackbar } from '@mui/material';
import { fetchOrderMetrics, recalcOrderMetrics } from '../services/orderMetricsService';
import RangeSelector from '../components/ui/RangeSelector';
import FiltersPanel from '../components/FiltersPanel';
import OrdersTable from '../components/Dashboard/tables/OrdersTable';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import esLocale from 'date-fns/locale/es';
import { useFilters } from '../hooks/useFilters';
import { useUrlParams } from '../hooks/useUrlParams';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import userDataService from '../services/userDataService';

// Monitoreo OAuth2
import { useOAuth2Monitor } from '../hooks/useOAuth2Monitor';
import OAuth2StatusPanel from '../components/monitoring/OAuth2StatusPanel';

// Iconos
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';

// KPI cards
import MetricCard from '../components/ui/MetricCard';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
// Gráficos avanzados
import OrdersRevenueArea from '../components/Dashboard/charts/OrdersRevenueArea';
import OrdersStatusPie from '../components/Dashboard/charts/OrdersStatusPie';

function Orders() {
  const theme = useTheme();
  const [exportOpen, setExportOpen] = useState(false);
  // Rango rápido seleccionado (today, yesterday, 7d, 30d, custom)
  const [rangeKey, setRangeKey] = useState('30d');
  // Obtenemos datos del usuario mediante múltiples fuentes para garantizar robustez
  const { userData, currentUser } = useAuth();
  
  // Monitoreo OAuth2 para alertas proactivas
  const { 
    metrics: oauth2Metrics, 
    isCritical, 
    hasWarnings, 
    getRecentErrors,
    isHealthy 
  } = useOAuth2Monitor();
  
  // Estrategia con múltiples fallbacks para el storeId
  // Primero intentamos con el servicio singleton, luego con valores del contexto
  const storeId = userDataService?.getStoreId?.() || 
                 userData?.tiendaId || 
                 userData?.tiendas?.[0]?.id || 
                 currentUser?.uid;

  // Estado para órdenes y UI
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [syncResult, setSyncResult] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const [prevMetrics, setPrevMetrics] = useState({});
  const [dailyRevenueSeries, setDailyRevenueSeries] = useState([]);
  
  // Monitoreo de errores OAuth2 para logging
  useEffect(() => {
    if (hasWarnings()) {
      const recentErrors = getRecentErrors(3);
      console.warn('[Orders] OAuth2 Warnings detected:', {
        systemHealth: oauth2Metrics.systemHealth,
        consecutiveErrors: oauth2Metrics.consecutiveErrors,
        errorRate: oauth2Metrics.errorRate,
        recentErrors: recentErrors
      });
    }
    
    if (isCritical()) {
      console.error('[Orders] OAuth2 Critical state detected:', {
        systemHealth: oauth2Metrics.systemHealth,
        consecutiveErrors: oauth2Metrics.consecutiveErrors,
        errorRate: oauth2Metrics.errorRate,
        totalRequests: oauth2Metrics.totalRequests,
        totalErrors: oauth2Metrics.totalErrors
      });
    }
  }, [hasWarnings, isCritical, oauth2Metrics, getRecentErrors]);
  
  // Cargar métricas pre-calculadas desde backend y fusionarlas con KPIs locales
  useEffect(() => {
    if (!storeId) return;
    let mounted = true;
    const loadMetrics = async () => {
      try {
        const res = await fetchOrderMetrics({ storeId, range: rangeKey });
        const list = res?.data?.metrics || [];
        if (!mounted) return;
        if (list.length) {
          setPrevMetrics((prev) => ({ ...prev, ...list[0] }));
    // Construir serie de ingresos diarios si backend lo devuelve
    if (list[0]?.dailyRevenue) {
      setDailyRevenueSeries(list[0].dailyRevenue);
    }
        } else {
          // Solicitar recálculo si no hay métricas
          try {
            await recalcOrderMetrics(storeId);
            const retry = await fetchOrderMetrics({ storeId, range: rangeKey });
            const retryList = retry?.data?.metrics || [];
            if (mounted && retryList.length) {
              setPrevMetrics((prev) => ({ ...prev, ...retryList[0] }));
          if (retryList[0]?.dailyRevenue) {
            setDailyRevenueSeries(retryList[0].dailyRevenue);
          }
            }
          } catch (reErr) {
            console.error('[Orders] Error recalc metrics', reErr);
          }
        }
      } catch (err) {
        console.error('[Orders] Error fetching order metrics', err);
      }
    };
    loadMetrics();
    return () => {
      mounted = false;
    };
  }, [storeId, rangeKey]);

  // Recalcular métricas rápidas cada vez que cambie la lista de órdenes
  useEffect(() => {
    if (!Array.isArray(orders) || orders.length === 0) {
      setPrevMetrics({});
      return;
    }

    const totalSales = orders.reduce((acc, o) => acc + (o.totalPrice ?? o.total ?? 0), 0);

    const statusMap = orders.reduce((acc, o) => {
      const st = String(o.status ?? '').toLowerCase();
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    }, {});

    const paymentMap = orders.reduce((acc, o) => {
      const pay = String(o.paymentStatus ?? o.payment ?? '').toLowerCase();
      acc[pay] = (acc[pay] || 0) + 1;
      return acc;
    }, {});

    setPrevMetrics({
      count: orders.length,
      total: totalSales.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }),
      statuses: statusMap,
      paymentStatuses: paymentMap,
    });
  }, [orders]);
  const [quickStatus, setQuickStatus] = useState('all');

  // Cargar información de última sincronización cada vez que cambie el estado de sincronización o el storeId
  useEffect(() => {
    const loadSyncInfo = async () => {
      try {
        if (storeId) {
          const info = await getLastSyncInfo(storeId);
          setLastSyncInfo(info);
        }
      } catch (err) {
        console.error('[Orders] Error al obtener info de sincronización:', err);
      }
    };

    loadSyncInfo();
  }, [storeId, syncStatus]);
  const [totalPages, setTotalPages] = useState(1); // Estado para paginación

  // Configuración de tabla
  const [orderBy, setOrderBy] = useState('createdAt');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10); // Fijado por ahora

  // Fallback local para serie de revenue diario
  const buildLocalRevenueSeries = useCallback((ordersArr = []) => {
    const map = {};
    ordersArr.forEach((o) => {
      const createdAt = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      if (!createdAt || Number.isNaN(createdAt)) return;
      const key = createdAt.toISOString().split('T')[0];
      map[key] = (map[key] || 0) + Number(o.totalPrice || o.total || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([dateLabel, revenue]) => ({ dateLabel, revenue }));
  }, []);

  // Buscador simple
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filtros avanzados
  const { filters, setFilters, resetFilters } = useFilters();

  // Manejar cambio de rango rápido
  const handleRangeChange = ({ key, startDate, endDate }) => {
    setRangeKey(key);
    // Convertir strings yyyy-MM-dd a objetos Date
    const toDate = (str) => str ? new Date(str + 'T00:00:00') : null;
    setFilters({
      startDate: toDate(startDate),
      endDate: toDate(endDate)
    });
    // Reiniciar paginación
    setPage(1);
  };

  // Sincronización con URL para bookmarking y compartir
  useUrlParams({
    page, setPage,
    status: quickStatus, setStatus: setQuickStatus,
    orderBy, setOrderBy,
    orderDirection, setOrderDirection,
    search: searchQuery, setSearch: setSearchQuery
  });

  // Calcular métricas de órdenes para mostrar en dashboard
  // Ref para identificar la última combinación de filtros/consulta en vuelo y descartar respuestas obsoletas
const latestQueryKey = useRef('');

const calculateMetrics = useCallback((ordersList) => {
    if (!Array.isArray(ordersList) || ordersList.length === 0) {
      return { 
        count: 0, 
        total: 0, 
        statuses: {},
        paymentStatuses: {},
        shippingStatuses: {}
      };
    }
    
    // Inicializamos contadores
    const metrics = {
      count: ordersList.length,
      total: 0,
      statuses: {},
      paymentStatuses: {},
      shippingStatuses: {}
    };
    
    // Procesamos cada orden
    ordersList.forEach(order => {
      // Total de ventas
      metrics.total += Number(order.totalPrice || 0);
      
      // Contadores de estados
      const status = (order.status || 'unknown').toLowerCase();
      metrics.statuses[status] = (metrics.statuses[status] || 0) + 1;
      
      const paymentStatus = (order.paymentStatus || 'unknown').toLowerCase();
      metrics.paymentStatuses[paymentStatus] = (metrics.paymentStatuses[paymentStatus] || 0) + 1;
      
      const shippingStatus = (order.shippingStatus || 'unknown').toLowerCase();
      metrics.shippingStatuses[shippingStatus] = (metrics.shippingStatuses[shippingStatus] || 0) + 1;
    });
    
    return metrics;
  }, []);

  /**
   * Carga órdenes desde Firestore y las normaliza usando el adaptador
   * Responsabilidad única (SRP): Cargar datos y actualizar estado
   * Manejo de errores: Tratamiento defensivo de casos borde
   */
  const loadOrders = useCallback(async () => {
        // Mapeo de quickStatus a status real (por ejemplo, 'pending' → 'open')
        const mappedStatus = quickStatus === 'pending' ? 'open' : quickStatus;

        // Construir un hash de la consulta actual para control de concurrencia
    const queryKey = JSON.stringify({
      status: mappedStatus,
      startDate: filters.startDate?.toISOString?.() || null,
      endDate: filters.endDate?.toISOString?.() || null,
      page,
      orderBy,
      orderDirection,
      searchQuery
    });
    latestQueryKey.current = queryKey;
    try {
      // 1. Estrategia defensiva: Obtener storeId usando múltiples fuentes
      // Se implementa aquí nuevamente para garantizar el valor más actualizado
      const resolvedStoreId = userDataService?.getStoreId?.() || 
                           userData?.tiendaId || 
                           userData?.tiendas?.[0]?.id || 
                           currentUser?.uid;
      
      // Verificación estricta del ID de tienda
      if (!resolvedStoreId) {
        console.error('❌ Error: No se ha podido resolver el storeId (modo defensivo)');
        
        // Diagnóstico ampliado para debugging
        console.log('🔍 Diagnosticando problema de storeId:', {
          userDataServicePresent: !!userDataService,
          getStoreIdMethod: !!userDataService?.getStoreId,
          userDataPresent: !!userData,
          tiendaIdPresent: !!userData?.tiendaId,
          tiendasArrayPresent: !!userData?.tiendas,
          tiendasLength: userData?.tiendas?.length,
          currentUserPresent: !!currentUser,
          uidPresent: !!currentUser?.uid
        });
        
        setError('Error: No se ha podido obtener el ID de la tienda. Por favor, actualiza la página o vuelve a iniciar sesión.');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Verificación adicional para evitar datos incorrectos
      console.log(`⚠️ Verificando storeId: ${resolvedStoreId}`);
      
      // Parámetros de consulta - Modelo inmutable con validación
      const params = {
        storeId: resolvedStoreId,  // Usamos el ID resuelto de forma segura
        page,
        perPage: 10,
        status: mappedStatus !== 'all' ? mappedStatus : undefined,
        startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : undefined,
        orderBy,
        orderDirection,
        searchTerm: searchQuery || undefined,
        // Marcador para identificar la fuente real de datos
        source: 'firestore-direct'
      };

      console.log('🔍 Cargando órdenes de Firestore...', params);

      // 1. Repositorio: Obtención EXCLUSIVA de datos reales desde Firestore
      // Implementación con control estricto de fuentes
      console.log('🔒 Aplicando protocolo de seguridad de datos');
      const result = await fetchOrdersFromFirestore(params);
      
      // Verificación rigurosa de la fuente de datos
      const dataSource = result.metadata?.source;
      if (dataSource !== 'firestore') {
        console.error(`🚨 DATOS NO AUTORIZADOS: Fuente '${dataSource}' rechazada. Solo se permite 'firestore'`);
        setError(`Error de integridad: Se detectó una fuente de datos no autorizada (${dataSource})`);
        setOrders([]);
        setLoading(false);
        return;
      }

      // Verificación estructural y procesamiento solo con datos autorizados
      const rawOrders = result.orders || [];
      const totalPages = result.totalPages || 0;
      
      console.log(`📦 Órdenes verificadas de Firestore: ${rawOrders.length}`);
      
      if (!rawOrders.length) {
      console.log('📅 No se encontraron órdenes en la base de datos');
      if (latestQueryKey.current !== queryKey) {
        console.warn('↩️ Respuesta vacía obsoleta ignorada');
        return;
      }
      setOrders([]);
      setTotalPages(0);
      // Reiniciar métricas a valores vacíos
      setPrevMetrics(calculateMetrics([]));
      return;
    }
      
      // Copias para depuración - Solo datos verificados
      // Comprobar si la respuesta corresponde aún a los filtros vigentes
    if (latestQueryKey.current !== queryKey) {
      console.warn('↩️ Respuesta obsoleta ignorada');
      return;
    }

    window.__ordersDebugOriginal__ = [...rawOrders]; 
      
      // 3. Adaptador: Transformación de dominio con patrón adaptador
      console.log('⚙️ Normalizando órdenes con OrderAdapter');
      const normalizedOrders = OrderAdapter.normalizeMany(rawOrders);
      
      // Copia para depuración
      window.__ordersDebug__ = normalizedOrders;
      console.log('✅ Normalización completada');
      
      // 4. Actualizar estado React con datos normalizados
      // Verificar nuevamente antes de mutar estado
    if (latestQueryKey.current !== queryKey) return;

    setOrders(normalizedOrders);
    // Si no tiene serie de revenue y calculamos local
    if (dailyRevenueSeries.length === 0) {
      setDailyRevenueSeries(buildLocalRevenueSeries(normalizedOrders));
    }
    setTotalPages(totalPages || 1);
    setLastSyncInfo(result.lastSync);
      
      // 5. Cálculos derivados: Métricas
      const metrics = calculateMetrics(normalizedOrders);
      if (latestQueryKey.current === queryKey) {
      setPrevMetrics(metrics);
    }
    } catch (err) {
      console.error('❌ Error en loadOrders:', err);
      setError('Error al cargar órdenes: ' + err.message);
      // Reiniciar métricas y limpiar tabla para evitar valores obsoletos
      setPrevMetrics(calculateMetrics([]));
      setOrders([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [filters, orderBy, orderDirection, page, perPage, quickStatus, searchQuery, storeId]);
  
  // Efecto principal de carga de órdenes (única fuente de verdad)
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Manejar sincronización manual de órdenes
  // Por ahora siempre realizamos una sincronización completa para refrescar campos faltantes
  const handleExportCsv = () => {
    if (!orders.length) {
      alert('No hay órdenes disponibles para exportar.');
      return;
    }

    const headers = ['ID', 'Fecha', 'Cliente', 'Total', 'Estado', 'Pago', 'Envío'];

    const safeFormatDate = (value) => {
      if (!value) return '';
      let dateVal = null;
      try {
        if (typeof value === 'object') {
          // Firestore Timestamp u objeto similar
          if (typeof value.toDate === 'function') {
            dateVal = value.toDate();
          } else if ('seconds' in value) {
            dateVal = new Date(value.seconds * 1000);
          }
        }
        if (!dateVal) {
          dateVal = new Date(value);
        }
        if (!dateVal || isNaN(dateVal)) return '';
        return dateVal.toISOString().split('T')[0];
      } catch (err) {
        console.warn('[safeFormatDate] valor inválido:', value, err);
        return '';
      }
    };

    const rows = orders.map((o) => [
      o.id ?? '',
      safeFormatDate(o.createdAt || o.created_at || o.date),
      o.customer?.name || o.customer || '',
      typeof o.total === 'number' ? o.total.toFixed(2) : o.total ?? '',
      o.status ?? '',
      o.payment ?? '',
      o.shippingStatus ?? o.shipping ?? ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell).replace(/"/g, '""');
            return cellStr.includes(',') ? `"${cellStr}"` : cellStr;
          })
          .join(',')
      )
      .join('\n');

    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordenes_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportOpen(true);
    } catch (err) {
      console.error('[Orders] Error exportando CSV:', err);
      alert('Error al generar CSV. Revisa la consola para más detalles.');
    }
  };



  const handleSyncOrders = async () => {
    try {
      // Actualizar UI inmediatamente
      setSyncStatus('syncing');
      setSyncResult(null);
      
      // Registrar inicio de sincronización para diagnóstico
      try {
        const diagData = {
          userId: userData?.uid,
          storeId,
          source: 'manual',
          startedAt: new Date().toISOString()
        };
        console.log('Diagnóstico sync:', diagData);
      } catch (e) {}
      
      // Ejecutar la sincronización total
      const syncResult = await syncAll(storeId);
      
      // Actualizar UI con resultado
      setSyncStatus('success');
      setSyncResult(syncResult);
      
      // Recargar órdenes para ver las actualizadas
      loadOrders();
    } catch (error) {
      console.error('Error sincronizando órdenes:', error);
      setSyncStatus('error');
      setSyncResult({
        error: true,
        message: error.message
      });
    }
  };

  // Manejar la apertura del detalle de una orden
  const handleOrderClick = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setDetailOpen(true);
    }
  };

  // Manejar cambio de ordenamiento
  const handleOrderByChange = (column) => {
    setOrderDirection(orderBy === column && orderDirection === 'asc' ? 'desc' : 'asc');
    setOrderBy(column);
  };

  // Filtrar por estado rápido
  const handleQuickFilterChange = (event, newStatus) => {
    if (newStatus !== null) {
      setQuickStatus(newStatus);
      setPage(1); // Resetear a primera página al cambiar filtro
    }
  };

  // Mostrar error o estado de carga
  if (error && !loading) {
    return (
      <Box p={3} textAlign="center">
        <Alert severity="error">
          {error}
          <Button 
            variant="outlined" 
            sx={{ ml: 2 }} 
            onClick={() => loadOrders()}
          >
            Reintentar
          </Button>
        </Alert>
      </Box>
    );
  }

  if (loading && orders.length === 0) {
    return <LoadingScreen message="Cargando órdenes..." />;
  }

  // Renderizar componente principal
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <Box sx={{ p: 3 }}>
        {/* Encabezado y controles */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={3}
        >
          {lastSyncInfo && (
            <Typography variant="body2" color="text.secondary">
              Último sync: {lastSyncInfo.lastSync ? new Date(lastSyncInfo.lastSync).toLocaleString() : '—'}
            </Typography>
          )}
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
            >
              Exportar CSV
            </Button>
            <Button
              variant="contained"
              startIcon={syncStatus === 'syncing' ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              disabled={syncStatus === 'syncing'}
              onClick={handleSyncOrders}
            >
              {syncStatus === 'syncing' ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          </Stack>
        </Stack>

        {/* Alertas OAuth2 */}
        {isCritical() && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Sistema de autenticación crítico:</strong> Se detectaron múltiples errores de OAuth2. 
            Revisa la conectividad con Tiendanube.
          </Alert>
        )}
        
        {hasWarnings() && !isCritical() && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Advertencia de autenticación:</strong> Se detectaron algunos errores de OAuth2. 
            El sistema está funcionando pero requiere atención.
          </Alert>
        )}
        
        {/* Panel de estado OAuth2 compacto */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <OAuth2StatusPanel compact={true} />
        </Box>

        {/* Selector rápido de rango de fechas */}
        <RangeSelector value={rangeKey} onChange={handleRangeChange} />

        {/* Filtro rápido por estado */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={quickStatus}
            exclusive
            onChange={handleQuickFilterChange}
            aria-label="filtro por estado"
            size="small"
          >
            <ToggleButton value="all">Todas</ToggleButton>
            <ToggleButton value="open">Abiertas</ToggleButton>
            <ToggleButton value="pending">Pendientes</ToggleButton>
            <ToggleButton value="authorized">Autorizadas</ToggleButton>
            <ToggleButton value="closed">Cerradas</ToggleButton>
            <ToggleButton value="in_dispute">En disputa</ToggleButton>
            <ToggleButton value="canceled">Canceladas</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {/* Panel de filtros avanzados */}
        <FiltersPanel 
          open={filtersOpen}
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
          onApply={() => {
            setFiltersOpen(false);
            setPage(1);
            loadOrders();
          }}
          onClose={() => setFiltersOpen(false)}
        />
        
        {/* Mostrar indicadores de sincronización */}
        {syncStatus === 'syncing' && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>Sincronizando órdenes desde Tiendanube...</Typography>
          </Box>
        )}
        
        {syncStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Sincronización completada exitosamente. 
            {syncResult && ` Se procesaron ${syncResult.totalProcessed || 0} órdenes.`}
          </Alert>
        )}
        
        {syncStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error durante la sincronización: {syncResult?.message || 'Error desconocido'}
          </Alert>
        )}
        
        {/* Métricas rápidas – estilo profesional */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Órdenes"
              value={String(prevMetrics.count || 0)}
              icon={<ShoppingBagIcon />} color={theme.palette.primary.main}
              
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Ventas Totales"
              value={prevMetrics.total || 0}
              icon={<MonetizationOnIcon />} color={theme.palette.success.main}
              
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Abiertas"
              value={String(prevMetrics.statuses?.open || 0)}
              icon={<PendingActionsIcon />} color={theme.palette.info.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Sin Pago"
              value={String(prevMetrics.paymentStatuses?.pending || 0)}
              icon={<CreditCardOffIcon />} color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        {/* Visualizaciones avanzadas */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            {/* Evolución de ventas diarias */}
            <OrdersRevenueArea data={dailyRevenueSeries} />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* Distribución de estados */}
            <OrdersStatusPie statusCounts={prevMetrics.statuses || {}} />
          </Grid>
        </Grid>

        {/* Tabla de órdenes */}
        <OrdersTable 
          orders={orders}
          loading={loading}
          orderBy={orderBy}
          orderDirection={orderDirection}
          onOrderByChange={handleOrderByChange}
          onOrderClick={handleOrderClick}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        
        {/* Detalle de orden - implementado como modal simple */}
        {detailOpen && selectedOrder && (
          <Dialog
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Orden {selectedOrder.orderNumber}
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setDetailOpen(false)}
                aria-label="close"
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">Información del Cliente</Typography>
                <Typography>Nombre: {selectedOrder.customer?.name}</Typography>
                <Typography>Email: {selectedOrder.customer?.email}</Typography>
                <Typography>Teléfono: {selectedOrder.customer?.phone}</Typography>
                
                <Typography variant="h6" sx={{ mt: 3 }}>Productos</Typography>
                {Array.isArray(selectedOrder.items) && selectedOrder.items.map((item, idx) => (
                  <Box key={idx} sx={{ mb: 1, p: 1, border: '1px solid #eee' }}>
                    <Typography>{item.name} x {item.quantity}</Typography>
                    <Typography variant="body2">${item.price}</Typography>
                  </Box>
                ))}
                
                <Box sx={{ mt: 3 }}>
                  <Typography><strong>Total:</strong> ${selectedOrder.totalPrice}</Typography>
                </Box>
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default Orders;
